import { useState } from 'react';
import { toast } from '../use-toast';
import { showErrorToast } from '../../utils/errorHandler';

/**
 * Custom hook to manage ride status updates and actions
 */
export const useRideActions = (ride, otpVerification, onActionSuccess) => {
  const [updating, setUpdating] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);

  const handleStatusUpdate = async (newStatus) => {
    if (!ride) return;
    
    // For deliveries, check OTP verification before start/complete
    if (ride.type === 'delivery') {
      if (newStatus === 'start') {
        if (!ride.pickupOTP?.verified) {
          otpVerification.promptForOTP('pickup');
          return;
        }
      } else if (newStatus === 'complete') {
        if (!ride.deliveryOTP?.verified) {
          otpVerification.promptForOTP('delivery');
          return;
        }
      }
    }
    
    // For cash payments on completion, confirm cash received
    if (newStatus === 'complete' && ride.paymentMethod === 'cash') {
      const confirmed = window.confirm(
        `Did the rider pay K${ride.price?.toFixed(2)} in cash?\n\n` +
        `Click OK if cash was received.\n` +
        `Click Cancel if cash was NOT received (will mark as dispute).`
      );
      
      setUpdating(true);
      
      try {
        const api = (await import('../../utils/api')).default;
        await api.post('/drivers/ride-action', {
          rideId: ride.id,
          action: newStatus,
          cashReceived: confirmed
        });

        if (confirmed) {
          toast({
            title: 'Ride Completed',
            description: `Commission of K${ride.commissionAmount?.toFixed(2) || '2.00'} deducted from wallet`,
          });
          if (onActionSuccess) onActionSuccess('complete', 'history');
        } else {
          toast({
            title: 'Payment Dispute',
            description: 'Ride marked as payment dispute. Admin will review.',
            variant: 'destructive',
          });
          if (onActionSuccess) onActionSuccess('dispute', 'dashboard');
        }
      } catch (error) {
        console.error('Error updating ride status:', error);
        showErrorToast(toast, error, 'Failed to update ride status');
      } finally {
        setUpdating(false);
      }
      
      return;
    }
    
    // For non-cash or non-completion actions
    setUpdating(true);
    
    try {
      const api = (await import('../../utils/api')).default;
      await api.post('/drivers/ride-action', {
        rideId: ride.id,
        action: newStatus
      });

      // Auto-open navigation when starting ride
      if (newStatus === 'start') {
        toast({
          title: '🚗 Ride Started!',
          description: 'Proceed to the drop-off location.',
        });
        // Navigation is handled by the in-app DriverNavigationView
      } else {
        toast({
          title: 'Status Updated',
          description: `Ride status updated to ${newStatus}`,
        });
      }

      // If ride is completed, show review modal
      if (newStatus === 'complete') {
        setShowReviewModal(true);
      } else {
        // Refresh ride data
        if (onActionSuccess) onActionSuccess(newStatus, 'refresh');
      }
    } catch (error) {
      console.error('Error updating ride status:', error);
      showErrorToast(toast, error, 'Failed to update ride status');
    } finally {
      setUpdating(false);
    }
  };

  const getNextAction = () => {
    switch (ride?.status) {
      case 'accepted':
        return { label: '🚀 Start Ride & Navigate', action: 'start' };
      case 'in_progress':
        return { label: '✅ Complete Ride', action: 'complete' };
      default:
        return null;
    }
  };

  return {
    updating,
    showReviewModal,
    setShowReviewModal,
    handleStatusUpdate,
    getNextAction,
  };
};
